import { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

import { AttemptChargeParams, PaymentResponse } from "react-migration/components/StripePayment/typings";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { NumericInput } from "react-migration/components/NumericInput";
import StripePayment from "react-migration/components/StripePayment/StripePayment";
import { logEvent } from "react-migration/lib/util/logEvent";
import { Loading } from "react-migration/components/Loading";
import { ENVIRONMENT } from "src/js/util/environment";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import Feature from "src/js/stores/user/Feature";

import GET_ACCOUNT from "../../../apollo/queries/account.gql";
import { useTopupBalanceMutation } from "../../../hooks/useTopupBalanceMutation";
import { UKQuickOptions } from "./components/UKQuickOptions";
import { USQuickOptions } from "./components/USQuickOptions";
import { updateMapSettings } from "src/js/stores/map/actions/updateMapSettings";

export interface TopupFlowProps {
  initialAmount?: number;
  minTopupAmount?: number;
  onPaymentComplete?: () => void;
  onError?: (error: string) => void;
}

export const TopupFlow = ({ initialAmount, minTopupAmount, onPaymentComplete, onError }: TopupFlowProps) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState<number | undefined>(initialAmount);
  const [errorMessage, setErrorMessage] = useState("");
  const [topupBalance, { loading }] = useTopupBalanceMutation();
  const [amountError, setAmountError] = useState("");
  const [paymentPending, setPaymentPending] = useState(false);
  const currency = hasFeature(Feature.usAccess) ? "$" : "£";

  const attemptCharge = (params: AttemptChargeParams, onCompleted: (res: PaymentResponse) => void) => {
    setPaymentPending(true);
    updateMapSettings("paymentProcessing", true);
    topupBalance({
      variables: {
        pence: params.amount,
        stripeCharge: params.stripeCharge,
      },
      onCompleted: (res) => {
        onCompleted({
          id: res.topupBalance.id,
          status: res.topupBalance.status,
          error_message: res.topupBalance.error_message,
          client_secret: res.topupBalance.client_secret,
        });
        setPaymentPending(false);
        updateMapSettings("paymentProcessing", false);
        logEvent("Top Up Complete", { amount: params.amount / 100, location: "modal" });
      },
      onError: (res) => {
        setErrorMessage(res.message);
        setPaymentPending(false);
        updateMapSettings("paymentProcessing", false);
        logEvent("Top Up Error", { message: res.message, location: "modal" });
        onError?.(res.message);
      },
    });
  };

  const queriesToInvalidate = [GET_ACCOUNT];

  const handleAmountValueChange = (value: number | undefined) => {
    if (value && minTopupAmount && value < minTopupAmount) {
      setAmount(minTopupAmount);
    } else if (!value) {
      setAmountError("Amount required");
    } else {
      setAmountError("");
      setAmount(value);
    }
  };

  return (
    <Elements stripe={loadStripe(ENVIRONMENT.STRIPE_PUBLIC_KEY || "")}>
      <div className="flex justify-center text-md p-4">
        <div className="flex flex-col max-w-[500px]">
          <div className="mb-4">
            {paymentPending && <Loading />}
            {!paymentPending && (
              <>
                <NumericInput
                  value={amount}
                  onChange={(v) => handleAmountValueChange(v)}
                  onKeyUp={(v) => handleAmountValueChange(Number(v.currentTarget.value))}
                  placeholder="0"
                  label={t("dashboard.topup.topup.amount")}
                  errorMessage={amountError}
                  units={currency}
                  unitsPrefix={true}
                  hideSteppers={true}
                  minValue={minTopupAmount}
                />
                {hasFeature(Feature.usAccess) ? (
                  <USQuickOptions handleAmountValueChange={handleAmountValueChange} />
                ) : (
                  <UKQuickOptions handleAmountValueChange={handleAmountValueChange} />
                )}
              </>
            )}
          </div>
          <StripePayment
            amount={amount || 0}
            attemptCharge={attemptCharge}
            setPaymentComplete={onPaymentComplete}
            queriesToInvalidate={queriesToInvalidate}
            errorMessage={errorMessage}
            isFormInvalid={Boolean(amountError)}
            loading={loading}
          />
        </div>
      </div>
    </Elements>
  );
};
